import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import CabPage from "../CabPage";
import MobileNav from "../account/MobileNav";
import { Icon } from "../account/icons";
import { mediaUrl } from "../account/media";
import { Dialog, useToast } from "../fleet/ui";
import "../css/companies.css";

const API = process.env.REACT_APP_API_URL;
const auth = () => ({ Authorization: `JWT ${window.localStorage.getItem("access")}` });

const ROLES = { 1: "Грузовладелец", 2: "Перевозчик", 3: "Экспедитор", 4: "Экспедитор-перевозчик" };
const TYPES = { 1: "ОсОО", 2: "ИП", 3: "ОАО" };
const CARRIER_ROLES = [2, 4];

const num = (v) => {
  const m = String(v ?? "").match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
};

const fmt = (n) => Number(n).toLocaleString("ru-RU");

const SORTS = {
  orders: (a, b) => (b.active_orders_count || 0) - (a.active_orders_count || 0),
  fleet: (a, b) => (num(b.vehicles_numbers) || 0) - (num(a.vehicles_numbers) || 0),
  name: (a, b) => (a.company_name || "").localeCompare(b.company_name || "", "ru"),
};

const plural = (n, one, few, many) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

const EMPTY = { role: "", country: "", verified: "", fleet: "" };

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("orders");
  const [dialog, setDialog] = useState(null); // { kind: "profile" | "write" | "more", company }
  const [partners, setPartners] = useState([]);
  const [toast, toastNode] = useToast();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [list, par] = await Promise.all([
          axios.get(`${API}/auth/companies/`, { headers: auth() }),
          axios.get(`${API}/auth/partners/`, { headers: auth() }).catch(() => ({ data: { companies: [] } })),
        ]);
        const rows = Array.isArray(list.data) ? list.data : list.data?.results || [];
        if (alive) {
          setCompanies(rows);
          setPartners(par.data?.companies || []);
        }
      } catch (e) {
        if (alive) setError("Не удалось загрузить список компаний.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // wireframe: the topbar search filters the list as you type (no navigation on Enter)
  useEffect(() => {
    const input = document.getElementById("global-search");
    const onInput = () => setQ(input.value);
    const onSearch = (e) => e.preventDefault();
    input?.addEventListener("input", onInput);
    window.addEventListener("cab:global-search", onSearch);
    return () => {
      input?.removeEventListener("input", onInput);
      window.removeEventListener("cab:global-search", onSearch);
    };
  }, []);

  const countries = useMemo(
    () => [...new Set(companies.map((c) => c.country).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ru")),
    [companies],
  );

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    return companies
      .filter((c) => {
        if (s) {
          const hay = [c.company_name, c.description, c.city, c.country, c.address, c.website, ROLES[c.role], TYPES[c.type]]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!hay.includes(s)) return false;
        }
        if (f.role && String(c.role) !== f.role) return false;
        if (f.country && c.country !== f.country) return false;
        if (f.verified === "yes" && !c.verified) return false;
        if (f.verified === "no" && c.verified) return false;
        if (f.fleet) {
          const n = num(c.vehicles_numbers);
          if (!CARRIER_ROLES.includes(c.role) || n == null) return false;
          if (f.fleet === "<100" ? n >= 100 : n < 100) return false;
        }
        return true;
      })
      .sort(SORTS[sort]);
  }, [companies, q, f, sort]);

  const defaults = !q.trim() && JSON.stringify(f) === JSON.stringify(EMPTY);
  const totalOrders = companies.reduce((s, c) => s + (c.active_orders_count || 0), 0);
  const verifiedCount = companies.filter((c) => c.verified).length;

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const reset = () => {
    setF(EMPTY);
    setQ("");
    const input = document.getElementById("global-search");
    if (input) input.value = "";
  };

  const togglePartner = useCallback(
    async (c) => {
      const has = partners.includes(c.id);
      try {
        if (has) await axios.delete(`${API}/auth/partners/${c.id}/`, { headers: auth() });
        else await axios.post(`${API}/auth/partners/`, { company: c.id }, { headers: auth() });
        setPartners((p) => (has ? p.filter((x) => x !== c.id) : [...p, c.id]));
        toast(has ? "Компания удалена из списка партнёров" : "Компания добавлена в список партнёров");
      } catch {
        toast("Не удалось изменить список партнёров");
      }
    },
    [partners, toast],
  );

  const close = useCallback(() => setDialog(null), []);

  const metrics = [
    { icon: "building", value: fmt(companies.length), label: ["Компаний", "в каталоге"] },
    { icon: "shield", value: fmt(verifiedCount), label: ["Проверенные", "компании"] },
    { icon: "truck", value: fmt(totalOrders), label: ["Активных", "заказов"] },
  ];
  const bars = [13, 23, 27, 36, 48];

  const profileFields = (c) =>
    [
      ["Форма", TYPES[c.type]],
      ["Страна", c.country],
      ["Город", c.city],
      ["Адрес", c.address],
      ["Телефон", c.phone],
      ["Email", c.email],
      ["Сайт", c.website],
      ["Год основания", c.founded_year],
      ["Сфера деятельности", c.industry],
      ["Сотрудников", c.employers_number],
      ["Транспортных средств", c.vehicles_numbers],
      ["Заказов в месяц", c.orders_month],
      ["Активных заказов", c.active_orders_count],
    ].filter(([, v]) => v !== null && v !== undefined && v !== "");

  return (
    <CabPage name="companies">
      <main id="main">
        <section className="page-heading">
          <div className="heading-copy">
            <h1>Компании</h1>
            <p>
              Найдите надежных перевозчиков, экспедиторов, диспетчеров и грузоотправителей
              <br />
              для долгосрочного сотрудничества
            </p>
          </div>
          <div className="company-metrics">
            {metrics.map((m, i) => (
              <article key={i} className={`metric metric-${i}`}>
                <Icon name={m.icon} />
                <b>{loading ? "—" : m.value}</b>
                <p>
                  {m.label[0]}
                  <br />
                  {m.label[1]}
                </p>
                <span className="metric-bars">
                  {bars.map((h) => (
                    <i key={h} style={{ height: `${h}px` }}></i>
                  ))}
                </span>
              </article>
            ))}
          </div>
        </section>

        <form
          id="company-filters"
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="filter-grid">
            <label className="filter-field">
              <span>Роль компании</span>
              <span className="select-wrap">
                <Icon name="route" />
                <select aria-label="Роль компании" name="role" value={f.role} onChange={set("role")}>
                  <option value="">Все типы</option>
                  {Object.entries(ROLES).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
                <Icon name="chevron" />
              </span>
            </label>
            <label className="filter-field" title="Скоро: у компаний пока нет списка направлений">
              <span>Направления/специализация</span>
              <span className="select-wrap">
                <Icon name="route" />
                <select aria-label="Направления" name="region" disabled>
                  <option>Скоро</option>
                </select>
                <Icon name="chevron" />
              </span>
            </label>
            <label className="filter-field">
              <span>Страна</span>
              <span className="select-wrap">
                <Icon name="globe" />
                <select aria-label="Страна" name="country" value={f.country} onChange={set("country")}>
                  <option value="">Все страны</option>
                  {countries.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <Icon name="chevron" />
              </span>
            </label>
            <label className="filter-field">
              <span>Статус проверки</span>
              <span className="select-wrap">
                <Icon name="shield" />
                <select aria-label="Статус проверки" name="verified" value={f.verified} onChange={set("verified")}>
                  <option value="">Все компании</option>
                  <option value="yes">Проверенные</option>
                  <option value="no">Не проверенные</option>
                </select>
                <Icon name="chevron" />
              </span>
            </label>
            <label className="filter-field">
              <span>Размер автопарка</span>
              <span className="select-wrap">
                <Icon name="truck" />
                <select aria-label="Размер автопарка" name="fleet" value={f.fleet} onChange={set("fleet")}>
                  <option value="">Любой</option>
                  <option value="<100">До 100 машин</option>
                  <option value="100+">От 100 машин</option>
                </select>
                <Icon name="chevron" />
              </span>
            </label>
            <label className="filter-field" title="Скоро: рейтинги компаний пока не ведутся">
              <span>Рейтинг</span>
              <span className="select-wrap">
                <Icon name="star" />
                <select aria-label="Рейтинг" name="rating" disabled>
                  <option>Скоро</option>
                </select>
                <Icon name="chevron" />
              </span>
            </label>
          </div>
          <div className="filter-footer">
            <div style={{ marginLeft: "auto" }}>
              <button className="button reset" type="button" onClick={reset}>
                Сбросить
              </button>
              <button className="button primary" type="submit">
                <Icon name="search" />
                Найти компании
              </button>
            </div>
          </div>
        </form>

        <div className="results-heading">
          <h2 id="result-title">
            {loading
              ? "Загрузка…"
              : defaults
                ? `Найдено ${fmt(filtered.length)} ${plural(filtered.length, "компания", "компании", "компаний")}`
                : `Найдено: ${fmt(filtered.length)}`}
          </h2>
          <label className="sort">
            <span>Сортировка:</span>
            <select aria-label="Сортировка" id="sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="orders">По активным заказам</option>
              <option value="fleet">По автопарку</option>
              <option value="name">По названию</option>
            </select>
            <Icon name="chevron" />
          </label>
        </div>

        <section aria-label="Каталог компаний" id="company-list">
          {filtered.map((c) => {
            const roleLabel = ROLES[c.role];
            const place = [c.city, c.country].filter(Boolean).join(", ");
            const desc = c.description || [place, c.address, c.website].filter(Boolean).join(" · ");
            const hasFleet = c.vehicles_numbers != null && c.vehicles_numbers !== "";
            const isPartner = partners.includes(c.id);
            return (
              <article className="company" key={c.id} data-id={c.id}>
                <div className="company-main">
                  {c.profile_images ? (
                    <img className="company-logo" alt={c.company_name} src={mediaUrl(c.profile_images)} style={{ objectFit: "cover", width: "clamp(48px, 5.1vw, 73px)", height: "clamp(48px, 5.1vw, 73px)" }} />
                  ) : (
                    <span
                      className="company-logo"
                      role="img"
                      aria-label={c.company_name}
                      style={{ width: "clamp(48px, 5.1vw, 73px)", height: "clamp(48px, 5.1vw, 73px)", display: "grid", placeItems: "center", background: "#eaf2ff", color: "#0063ff" }}
                    >
                      <Icon name="building" className="icon" style={{ width: "55%", height: "55%" }} />
                    </span>
                  )}
                  <div className="company-description">
                    <div className="company-title">
                      <h2>{c.company_name || "Без названия"}</h2>
                      {c.verified && <span className="verified-icon">✓</span>}
                      {c.verified && <span className="verified-label">◉ Проверенная компания</span>}
                    </div>
                    <p>{desc || "Описание не заполнено"}</p>
                    <div className="tags">
                      {roleLabel && <span>{roleLabel}</span>}
                      {TYPES[c.type] && <span>{TYPES[c.type]}</span>}
                      {c.country && <span>{c.country}</span>}
                    </div>
                  </div>
                </div>
                <div className="fleet-stat">
                  <Icon name="truck" />
                  <div>
                    <b>{hasFleet ? c.vehicles_numbers : "—"}</b>
                    <small>Грузовых машин</small>
                    <small>{c.employers_number ? `Сотрудников: ${c.employers_number}` : " "}</small>
                  </div>
                </div>
                <div className="directions">
                  <Icon name="route" />
                  <div>
                    <strong>{c.city || c.country || "—"}</strong>
                    <small>{c.city && c.country ? c.country : "Расположение"}</small>
                  </div>
                </div>
                <div className="rating">
                  <b>{c.active_orders_count || 0}</b>
                  <small>Активных заказов</small>
                  <small>{c.founded_year ? `На рынке с ${c.founded_year}` : c.orders_month ? `Заказов в месяц: ${c.orders_month}` : " "}</small>
                </div>
                <div className="company-actions">
                  <button className="button primary" type="button" onClick={() => setDialog({ kind: "profile", company: c })}>
                    Открыть профиль
                  </button>
                  <button aria-pressed={isPartner} className="button partner" type="button" onClick={() => togglePartner(c)}>
                    <Icon name="users" />
                    {isPartner ? "✓ В партнёрах" : "＋ Добавить в партнёры"}
                  </button>
                </div>
                <button aria-label={`Действия с ${c.company_name || "компанией"}`} className="button more" type="button" onClick={() => setDialog({ kind: "more", company: c })}>
                  ···
                </button>
              </article>
            );
          })}
        </section>

        {!loading && filtered.length === 0 && (
          <div className="empty">
            <h3>{error || "Компании не найдены"}</h3>
            {!error && <p>Попробуйте изменить фильтры.</p>}
            {!error && (
              <button className="button" type="button" onClick={reset}>
                Сбросить фильтры
              </button>
            )}
          </div>
        )}
      </main>

      {dialog?.kind === "profile" && (
        <Dialog title={dialog.company.company_name || "Компания"} onClose={close}>
          <div className="profile-content">
            <h3>{ROLES[dialog.company.role] || "Компания"}</h3>
            {dialog.company.description && <p>{dialog.company.description}</p>}
            <dl style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: "9px 12px", fontSize: 14 }}>
              {profileFields(dialog.company).map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt style={{ color: "#5b70a0" }}>{k}</dt>
                  <dd style={{ margin: 0 }}>{v}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        </Dialog>
      )}

      {dialog?.kind === "more" && (
        <Dialog title={dialog.company.company_name || "Компания"} onClose={close}>
          <button className="button" type="button" onClick={() => setDialog({ kind: "profile", company: dialog.company })}>
            Посмотреть профиль
          </button>
        </Dialog>
      )}

      {toastNode}
      <MobileNav />
    </CabPage>
  );
}
